import { useState } from 'react'
import {
  useCreateFlowBillingRecipient,
  useDeleteFlowBillingRecipient,
  useFlowBillingRecipients,
  useUpdateFlowBillingRecipient,
} from '../../../hooks/admin/useFlowBilling'
import { Card } from './fields'

/**
 * The billing recipient list.
 *
 * These are plain addresses, not user accounts: a recipient needs no IoAg
 * login, and adding one grants no access to the webapp. Rows persist as soon
 * as they change rather than staging with the rest of the form, matching the
 * system-health recipient editor.
 *
 * Disabling rather than deleting is the common case — a seasonal address that
 * should stop receiving reports without being retyped next spring — so the
 * toggle is the primary control and Remove is a plain text button.
 */

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function RecipientsSection() {
  const { data: recipients, isLoading } = useFlowBillingRecipients()
  const create = useCreateFlowBillingRecipient()
  const update = useUpdateFlowBillingRecipient()
  const remove = useDeleteFlowBillingRecipient()

  const [contact, setContact] = useState('')
  const [label, setLabel] = useState('')
  const [kind, setKind] = useState<'to' | 'cc'>('to')
  const [error, setError] = useState('')

  const enabledCount = (recipients ?? []).filter((r) => r.enabled).length

  const add = async () => {
    const value = contact.trim()
    if (!EMAIL_REGEX.test(value)) {
      setError('Enter a valid email address.')
      return
    }
    setError('')
    await create.mutateAsync({ contact: value, label: label.trim() || null, kind, enabled: true })
    setContact('')
    setLabel('')
  }

  return (
    <Card title="Recipients">
      <p className="text-sm text-gray-600 mb-4">
        Plain email addresses. A recipient does not need an IoAg login, and adding one grants no
        access to the webapp.
      </p>

      <div className="flex flex-wrap items-start gap-2 mb-2">
        <input
          type="email"
          className="flex-1 min-w-[220px] border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="reports@example.com"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
        />
        <input
          type="text"
          className="w-40 border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <select
          className="border border-gray-300 rounded px-3 py-2 text-sm"
          value={kind}
          onChange={(e) => setKind(e.target.value as 'to' | 'cc')}
        >
          <option value="to">To</option>
          <option value="cc">Cc</option>
        </select>
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          disabled={create.isPending}
          onClick={add}
        >
          {create.isPending ? 'Adding…' : 'Add'}
        </button>
      </div>
      {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

      {isLoading && <p className="text-sm text-gray-500">Loading recipients…</p>}

      {!isLoading && (recipients ?? []).length === 0 && (
        <p className="text-sm text-gray-500 py-3">
          No recipients yet. The report cannot be enabled until at least one is added and enabled.
        </p>
      )}

      {(recipients ?? []).length > 0 && (
        <ul className="divide-y divide-gray-200 border-t border-gray-200">
          {(recipients ?? []).map((recipient) => (
            <li key={recipient.id} className="flex items-center gap-3 py-2">
              <input
                type="checkbox"
                className="rounded border-gray-300 h-4 w-4"
                checked={recipient.enabled}
                onChange={(e) =>
                  update.mutate({ id: recipient.id, input: { enabled: e.target.checked } })
                }
                aria-label={`Enable ${recipient.contact}`}
              />
              <span
                className={`flex-1 text-sm truncate ${
                  recipient.enabled ? 'text-gray-900' : 'line-through text-gray-400'
                }`}
              >
                {recipient.contact}
                {recipient.label && (
                  <span className="text-gray-500 ml-2 text-xs">{recipient.label}</span>
                )}
              </span>
              <select
                className="border border-gray-300 rounded px-2 py-1 text-xs"
                value={recipient.kind}
                onChange={(e) =>
                  update.mutate({
                    id: recipient.id,
                    input: { kind: e.target.value as 'to' | 'cc' },
                  })
                }
                aria-label={`Delivery kind for ${recipient.contact}`}
              >
                <option value="to">To</option>
                <option value="cc">Cc</option>
              </select>
              <button
                type="button"
                className="text-red-600 hover:underline text-sm"
                onClick={() => remove.mutate(recipient.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-gray-500 mt-3">
        {enabledCount} enabled
        {(recipients ?? []).length - enabledCount > 0 &&
          ` · ${(recipients ?? []).length - enabledCount} disabled`}
        . At least one is required: while the report is on, the last enabled recipient cannot be
        disabled or removed.
      </p>
    </Card>
  )
}
